/**
 * Shared popup header — one slim header for module popups, with minimize,
 * maximize/restore and close. Every popup paints the shared module masthead
 * surface (`module-header`, index.css — deep state gold in light mode, navy in
 * dark), so popups match the page headers in every module. The `gradient`
 * prop is still accepted from older call sites but no longer changes colour.
 *
 * Shape: ~44px bar, 28px icon tile, 14px title, 10px subtitle, 28px icon buttons.
 *
 * Minimize hides the popup and its backdrop but keeps it mounted (a half-filled
 * form survives) and docks a small bar bottom-right with Restore and Close.
 * Maximize fills the viewport with no margin or rounded corners.
 *
 * Tailwind popup:
 *   const win = usePopupWindow(isOpen);
 *   <div className={win.overlayClass('fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4')}>
 *     <div className={win.panelClass('bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col')}>
 *       <PopupHeader window={win} gradient={MY_MODULE_GRADIENT} icon="ri-file-line" title="Title" onClose={onClose} />
 *       ...body...
 *
 * MUI <Dialog>:
 *   <Dialog open={open} onClose={onClose} {...win.dialogProps()}>
 *     <PopupHeader window={win} gradient={...} title="Title" onClose={onClose} />
 *
 * Popups that already had their own fullscreen state pass it in:
 *   usePopupWindow(isOpen, { isMaximized: isFullscreen, setMaximized: setIsFullscreen })
 *
 * Small "Are you sure?" confirms, lightboxes and dropdown popovers do not use this.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/** The one popup header surface: the shared module masthead. */
export const POPUP_DEFAULT_GRADIENT = 'module-header';

export const POPUP_HEADER_SHAPE =
  'relative isolate flex-shrink-0 flex items-center justify-between gap-3 px-4 py-1.5 text-white border-b border-white/10';

export const POPUP_ICON =
  'w-7 h-7 rounded-md bg-white/15 ring-1 ring-inset ring-white/20 flex items-center justify-center flex-shrink-0 text-white text-base';

export const POPUP_TITLE = 'text-sm font-semibold text-white tracking-tight leading-tight truncate';
export const POPUP_SUBTITLE = 'text-[10px] leading-tight text-white/75 truncate';

/** 28px icon-only button on the coloured bar. */
export const POPUP_ICON_BTN =
  'w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 text-white/75 hover:text-white hover:bg-white/15 transition-colors disabled:opacity-40 disabled:pointer-events-none';

/** 28px labelled button on the coloured bar (e.g. "Print"). */
export const POPUP_BTN =
  'h-7 px-2.5 rounded-md inline-flex items-center gap-1.5 flex-shrink-0 bg-white/10 ring-1 ring-inset ring-white/20 hover:bg-white/20 text-white text-[11px] font-semibold transition-colors';

const MAXIMIZED_OVERLAY = '!p-0';
/**
 * The shell sidebar is z-[6000]. A popup on a lower layer (z-50 etc.) is fine while
 * centred, but once maximized the sidebar paints over its left edge — so lift it.
 */
const SIDEBAR_Z = 6000;
const ABOVE_SIDEBAR = '!z-[7000]';
function belowSidebar(classes: string): boolean {
  const m = classes.match(/(?:^|\s)z-(?:\[(\d+)\]|(\d+))(?=\s|$)/);
  return !m || Number(m[1] ?? m[2]) < SIDEBAR_Z;
}

/**
 * Pinned to all four edges rather than sized to 100vw x 100vh and centred: a
 * centred viewport-sized panel can land a few px off (overlay padding, inline-block
 * baseline spacers, vh vs. the visible viewport), which left a gap above the header.
 */
const MAXIMIZED_PANEL =
  '!fixed !inset-0 !w-auto !h-auto !max-w-none !max-h-none !rounded-none !m-0 !transform-none';

export interface PopupWindow {
  isMaximized: boolean;
  isMinimized: boolean;
  toggleMaximize: () => void;
  minimize: () => void;
  restore: () => void;
  /** Adds hide-on-minimize and no-padding-on-maximize to the backdrop's classes. */
  overlayClass: (base: string) => string;
  /** Adds full-viewport sizing on maximize to the popup panel's classes. */
  panelClass: (base: string) => string;
  /** Props to spread on an MUI <Dialog>: fullScreen on maximize, hidden on minimize. */
  dialogProps: () => { fullScreen?: boolean; disableEnforceFocus?: boolean; sx?: Record<string, unknown> };
}

interface ExternalMaximize {
  isMaximized: boolean;
  setMaximized: (value: boolean) => void;
}

export function usePopupWindow(open = true, external?: ExternalMaximize): PopupWindow {
  const [ownMaximized, setOwnMaximized] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  const isMaximized = external ? external.isMaximized : ownMaximized;
  const setMaximized = external ? external.setMaximized : setOwnMaximized;

  const hasExternal = !!external;
  useEffect(() => {
    if (!open) {
      setIsMinimized(false);
      if (!hasExternal) setOwnMaximized(false);
    }
  }, [open, hasExternal]);

  // An open MUI Dialog/Drawer keeps the body scroll lock while hidden by minimize,
  // leaving the page behind unscrollable. Release it while minimized and put it
  // back on restore — but not when the popup is closing, since MUI restores the
  // original body style itself then.
  const openRef = useRef(open);
  openRef.current = open;
  useEffect(() => {
    if (!isMinimized) return;
    const body = document.body.style;
    if (body.overflow !== 'hidden') return;
    const saved = { overflow: body.overflow, paddingRight: body.paddingRight };
    body.overflow = '';
    body.paddingRight = '';
    return () => {
      if (openRef.current && body.overflow === '') {
        body.overflow = saved.overflow;
        body.paddingRight = saved.paddingRight;
      }
    };
  }, [isMinimized]);

  const toggleMaximize = useCallback(() => setMaximized(!isMaximized), [isMaximized, setMaximized]);
  const minimize = useCallback(() => setIsMinimized(true), []);
  const restore = useCallback(() => setIsMinimized(false), []);

  const overlayClass = useCallback(
    (base: string) =>
      [base, isMinimized && '!hidden', isMaximized && MAXIMIZED_OVERLAY, isMaximized && belowSidebar(base) && ABOVE_SIDEBAR]
        .filter(Boolean)
        .join(' '),
    [isMinimized, isMaximized],
  );
  const panelClass = useCallback(
    (base: string) => [base, isMaximized && MAXIMIZED_PANEL].filter(Boolean).join(' '),
    [isMaximized],
  );
  const dialogProps = useCallback(
    () => ({
      ...(isMaximized ? { fullScreen: true } : {}),
      ...(isMinimized ? { disableEnforceFocus: true, sx: { display: 'none' } } : {}),
    }),
    [isMaximized, isMinimized],
  );

  return { isMaximized, isMinimized, toggleMaximize, minimize, restore, overlayClass, panelClass, dialogProps };
}

const DOCK_ID = 'popup-dock';

/** One shared bottom-right column so several minimized popups stack instead of overlapping. */
function getDock(): HTMLElement {
  let dock = document.getElementById(DOCK_ID);
  if (!dock) {
    dock = document.createElement('div');
    dock.id = DOCK_ID;
    dock.className = 'fixed bottom-4 right-4 z-[12000] flex flex-col-reverse items-end gap-2 pointer-events-none';
    document.body.appendChild(dock);
  }
  return dock;
}

export interface PopupHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Remix icon class, e.g. "ri-building-line". */
  icon?: string;
  onClose?: () => void;
  /** From usePopupWindow(); omit to show only the close button. */
  window?: PopupWindow;
  /** Popup-specific buttons, rendered before minimize / maximize / close. */
  actions?: ReactNode;
  closeDisabled?: boolean;
  /** @deprecated Ignored — every popup uses the shared masthead surface. */
  gradient?: string;
  className?: string;
}

export function PopupHeader({
  title,
  subtitle,
  icon,
  onClose,
  window: win,
  actions,
  closeDisabled,
  className,
}: PopupHeaderProps) {
  const barClass = [POPUP_HEADER_SHAPE, POPUP_DEFAULT_GRADIENT, className].filter(Boolean).join(' ');
  return (
    <>
      <div className={barClass}>
        <div className="flex items-center gap-2 min-w-0">
          {icon && (
            <div className={POPUP_ICON}>
              <i className={icon} />
            </div>
          )}
          <div className="min-w-0">
            <h2 className={POPUP_TITLE}>{title}</h2>
            {subtitle && <p className={POPUP_SUBTITLE}>{subtitle}</p>}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {actions}
          {win && (
            <>
              <button type="button" onClick={win.minimize} className={POPUP_ICON_BTN} title="Minimize">
                <i className="ri-subtract-line text-lg" />
              </button>
              <button
                type="button"
                onClick={win.toggleMaximize}
                className={POPUP_ICON_BTN}
                title={win.isMaximized ? 'Restore' : 'Maximize'}
              >
                <i className={`${win.isMaximized ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'} text-base`} />
              </button>
            </>
          )}
          {onClose && (
            <button type="button" onClick={onClose} disabled={closeDisabled} className={POPUP_ICON_BTN} title="Close">
              <i className="ri-close-line text-lg" />
            </button>
          )}
        </div>
      </div>

      {win && (
        <PopupDock window={win} title={title} icon={icon} gradient={POPUP_DEFAULT_GRADIENT} onClose={onClose} closeDisabled={closeDisabled} />
      )}
    </>
  );
}

/**
 * The bottom-right bar a minimized popup leaves behind. PopupHeader renders it
 * itself; use it directly only for a popup that keeps its own header (e.g. a
 * drawer with a back button and breadcrumb) but still needs minimize.
 */
export function PopupDock({
  window: win,
  title,
  icon,
  gradient = POPUP_DEFAULT_GRADIENT,
  onClose,
  closeDisabled,
}: {
  window: PopupWindow;
  title: ReactNode;
  icon?: string;
  gradient?: string;
  onClose?: () => void;
  closeDisabled?: boolean;
}) {
  if (!win.isMinimized) return null;
  return createPortal(
    <div className={`pointer-events-auto flex items-center gap-2 pl-3 pr-1 py-1 rounded-lg shadow-xl ring-1 ring-white/10 text-white max-w-xs ${gradient}`}>
      {icon && <i className={`${icon} text-sm text-white/80`} />}
      <button type="button" onClick={win.restore} className="text-xs font-semibold truncate text-left hover:underline" title="Restore">
        {title}
      </button>
      <button type="button" onClick={win.restore} className={POPUP_ICON_BTN} title="Restore">
        <i className="ri-window-line text-sm" />
      </button>
      {onClose && (
        <button type="button" onClick={onClose} disabled={closeDisabled} className={POPUP_ICON_BTN} title="Close">
          <i className="ri-close-line text-base" />
        </button>
      )}
    </div>,
    getDock(),
  );
}

export default PopupHeader;
